import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { formatTime, formatDateTime, isoDate } from "../lib/time";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { ArrowRight } from "@phosphor-icons/react";

const fmtSlot = (iso) => formatTime(iso);

/**
 * RescheduleModal — move an existing booking to a new slot.
 *
 * Reuses the SAME availability engine as create-booking (GET /api/availability)
 * and the SAME validation backend (PUT /api/bookings/{id}). Booking ID is
 * preserved — notes / history stay attached. Cancelled and completed
 * bookings are rejected by the backend with a 409.
 */
export default function RescheduleModal({
  open,
  onOpenChange,
  booking,        // BookingDetail (the one we're moving)
  stylists,
  onRescheduled,  // (updatedBooking) => void
}) {
  const [date, setDate] = useState("");
  const [stylistId, setStylistId] = useState("");
  const [swapStylist, setSwapStylist] = useState(false);
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time we open with a new booking.
  useEffect(() => {
    if (open && booking) {
      setDate(isoDate(new Date(`${booking.start_at}`.slice(0, 10) + "T00:00:00")));
      setStylistId(booking.stylist_id);
      setSwapStylist(false);
      setSelectedSlot("");
      setSlots([]);
    }
  }, [open, booking]);

  // The stylist we'd reschedule TO — defaults to the booking's current stylist,
  // overridden when the receptionist toggles "Switch stylist".
  const targetStylistId = swapStylist ? stylistId : booking?.stylist_id;

  // Refresh available slots whenever the date or target stylist changes.
  useEffect(() => {
    if (!open || !booking || !date || !targetStylistId) return;
    let cancelled = false;
    setLoadingSlots(true);
    setSelectedSlot("");
    api
      .get("/availability", {
        params: {
          stylist_id: targetStylistId,
          service_id: booking.service_id,
          date,
          // Self-exclusion: the booking being rescheduled must not count
          // as a blocker against itself. Symmetric with PUT /api/bookings
          // /{id}'s exclude_booking_id; without it valid stagger slots
          // get hidden at the seams of the booking's current window.
          exclude_booking_id: booking.id,
        },
      })
      .then(({ data }) => {
        if (!cancelled) setSlots(data.slots || []);
      })
      .catch(() => {
        if (!cancelled) setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, booking, date, targetStylistId]);

  const eligibleStylists = useMemo(
    () => stylists.filter((s) => s.active && (!booking?.service?.stylist_ids?.length || booking.service.stylist_ids.includes(s.id))),
    [stylists, booking],
  );

  if (!booking) return null;

  const submit = async () => {
    if (!selectedSlot) {
      toast.error("Pick a new time");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { start_at: selectedSlot };
      if (swapStylist && stylistId && stylistId !== booking.stylist_id) {
        payload.stylist_id = stylistId;
      }
      const { data } = await api.put(`/bookings/${booking.id}`, payload);
      toast.success("Booking rescheduled");
      onRescheduled?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white border-border" data-testid="reschedule-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl tracking-tight">Reschedule</DialogTitle>
          <DialogDescription>
            Move <span className="font-medium">{booking.customer?.name}</span>'s {booking.service?.name}. The booking ID and history are preserved.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* Current slot — read-only context */}
          <div className="rounded-md border border-[#E0DBD0] bg-[#F4F1EA] px-4 py-3 flex items-center gap-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Currently</div>
            <div className="text-sm font-mono" data-testid="reschedule-current-slot">
              {formatDateTime(booking.start_at)} · {booking.stylist?.name}
            </div>
          </div>

          {/* Stylist swap toggle */}
          <div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
            <div>
              <Label className="text-sm font-medium">Switch stylist</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Off — keep with {booking.stylist?.name}. On — move to a different stylist.
              </p>
            </div>
            <Switch
              checked={swapStylist}
              onCheckedChange={setSwapStylist}
              data-testid="reschedule-swap-stylist-toggle"
            />
          </div>

          {swapStylist && (
            <div className="space-y-2">
              <Label className="overline">New stylist</Label>
              <Select value={stylistId} onValueChange={setStylistId}>
                <SelectTrigger className="bg-[#F9F8F6]" data-testid="reschedule-stylist-select">
                  <SelectValue placeholder="Select stylist" />
                </SelectTrigger>
                <SelectContent>
                  {eligibleStylists.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`reschedule-stylist-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Date picker */}
          <div className="space-y-2">
            <Label className="overline">New date</Label>
            <Input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="bg-[#F9F8F6] max-w-[220px]"
              data-testid="reschedule-date-input"
            />
          </div>

          {/* Slot picker */}
          <div className="space-y-2">
            <Label className="overline">Available slots</Label>
            {loadingSlots ? (
              <div className="text-sm text-muted-foreground" data-testid="reschedule-slots-loading">Loading...</div>
            ) : slots.length === 0 ? (
              <div className="text-sm text-muted-foreground" data-testid="reschedule-slots-empty">
                No slots available — try a different date or stylist.
              </div>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="reschedule-slots">
                {slots.map((s) => (
                  <button
                    key={s.start}
                    type="button"
                    onClick={() => setSelectedSlot(s.start)}
                    className={`px-3 py-1.5 rounded-md text-sm font-mono border transition-colors ${
                      selectedSlot === s.start
                        ? "bg-primary text-white border-primary"
                        : "bg-white border-border hover:border-primary"
                    }`}
                    data-testid={`reschedule-slot-${s.start}`}
                  >
                    {fmtSlot(s.start)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* New slot preview */}
          {selectedSlot && (
            <div className="rounded-md border border-primary/40 bg-primary/5 px-4 py-3 flex items-center gap-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Moving to</div>
              <div className="text-sm font-mono flex items-center gap-2" data-testid="reschedule-new-slot">
                <span>{formatDateTime(booking.start_at)}</span>
                <ArrowRight size={14} />
                <span className="font-medium">{formatDateTime(selectedSlot)}</span>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="reschedule-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!selectedSlot || submitting}
            className="bg-primary hover:bg-[#2B4F39]"
            data-testid="reschedule-submit-btn"
          >
            {submitting ? "Moving..." : "Reschedule"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
